import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { ApiError, apiFetch, apiJson } from '../../lib/api'
import { PageBody, PageHeader } from '../../components/PageHeader'
import { dateText, StockPreview, TagStockResponse } from './api'
import styles from './GodMode.module.css'

// Dataphyll's own tags (plastic tags and the like), item tags (DT…) or bin tags
// (DB…): codes with no client suffix, each checked against every code in use
// before it is made. A tag starts unused; the first client to scan it claims it.
export function TagStockPage() {
  const { user } = useAuth()
  const [data, setData] = useState<TagStockResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [quantity, setQuantity] = useState('')
  const [start, setStart] = useState('')
  const [width, setWidth] = useState('4')
  const [alphabet, setAlphabet] = useState('alphanumeric')
  const [kind, setKind] = useState('item')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [made, setMade] = useState<string | null>(null)
  const [preview, setPreview] = useState<StockPreview | null>(null)

  const load = useCallback(async () => {
    try {
      setData(await apiJson<TagStockResponse>(user, '/api/god-mode/tag-stock'))
    } catch (e: any) {
      setError(e.message)
    }
  }, [user])

  useEffect(() => {
    load()
  }, [load])

  const ask = () => ({ quantity: Number(quantity), start: start.trim() === '' ? null : start.trim().toUpperCase(), width: Number(width), alphabet, kind })
  // Any change to what is asked for means previewing again.
  const edit = (set: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    set(e.target.value)
    setPreview(null)
  }

  // Preview first: what would be made, written nowhere.
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    setMade(null)
    try {
      setPreview(await apiJson<StockPreview>(user, '/api/god-mode/tag-stock/preview', { method: 'POST', body: ask() }))
    } catch (err: any) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  // Then make exactly the batch the preview showed, or show the new one if it has changed.
  const make = async () => {
    if (!preview) return
    setBusy(true)
    setError(null)
    try {
      const r = await apiJson<StockPreview>(user, '/api/god-mode/tag-stock', {
        method: 'POST',
        body: { ...ask(), note: note || null, expect: { count: preview.count, first: preview.first, last: preview.last } },
      })
      setMade(`Made ${r.count} ${r.kind === 'bin' ? 'bin' : 'item'} tags, ${r.first} to ${r.last}.`)
      setPreview(null)
      setQuantity('')
      setStart('')
      setNote('')
      await load()
    } catch (err: any) {
      if (err instanceof ApiError && err.body?.code === 'STOCK_CHANGED' && err.body.preview) setPreview(err.body.preview)
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const download = async (batchId: string) => {
    setError(null)
    try {
      const res = await apiFetch(user, `/api/god-mode/tag-stock/${batchId}/csv`)
      if (!res.ok) throw new Error('The sheet could not be downloaded. Try again.')
      const url = URL.createObjectURL(await res.blob())
      const a = document.createElement('a')
      a.href = url
      a.download = `dataphyll-tags-${batchId.slice(0, 8)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err: any) {
      setError(err.message)
    }
  }

  return (
    <>
      <PageHeader title="Tag stock" />
      <PageBody>
        <div className={styles.stack}>
          <form className={`${styles.card} ${styles.form}`} onSubmit={submit}>
            <h2>New batch</h2>
            {made && <p className={styles.saved}>{made}</p>}
            <div className={styles.inline}>
              <label className={styles.field}>
                Tag type
                <select value={kind} onChange={edit(setKind)} aria-label="Tag type">
                  <option value="item">Item tags (DT…)</option>
                  <option value="bin">Bin tags (DB…)</option>
                </select>
              </label>
              <label className={styles.field}>
                How many
                <input type="number" min={1} max={data?.max ?? 10000} value={quantity} onChange={edit(setQuantity)} required />
              </label>
              <label className={styles.field}>
                Count in
                <select value={alphabet} onChange={edit(setAlphabet)}>
                  <option value="alphanumeric">Letters and numbers</option>
                  <option value="numeric">Numbers only</option>
                </select>
              </label>
              <label className={styles.field}>
                First ID
                <input value={start} onChange={edit(setStart)} placeholder="Next free" maxLength={12} />
              </label>
              <label className={styles.field}>
                Characters
                <input type="number" min={1} max={12} value={width} onChange={edit(setWidth)} required />
              </label>
            </div>
            <label className={styles.field}>
              Note
              <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="e.g. supplier order, colour" />
              <span className={styles.hint}>
                Letters and numbers count 0–9 then A–Z, leaving out I and O (0009, 000A … 000Z, 0010), as the old system did. IDs already in use
                anywhere are skipped. Item and bin tags each count on from their own highest ID. The sheet has the supplier's columns: Unique DATA, QR DATA.
              </span>
            </label>
            {preview && (
              <div aria-label="Preview">
                <p className={styles.lead}>
                  {preview.count === 0
                    ? 'No free numbers in that range.'
                    : `${preview.count} ${preview.kind === 'bin' ? 'bin' : 'item'} tags, ${preview.first} to ${preview.last}.`}
                  {preview.skipped.length > 0 &&
                    ` Passes over ${preview.skipped.length} already in use: ${preview.skipped.slice(0, 10).join(', ')}${preview.skipped.length > 10 ? '…' : ''}.`}
                </p>
                {preview.count > 0 && (
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Unique DATA</th>
                        <th>QR DATA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.head.map((r) => (
                        <tr key={r.qr}>
                          <td>{r.unique}</td>
                          <td><code>{r.qr}</code></td>
                        </tr>
                      ))}
                      {preview.count > preview.head.length + preview.tail.length && (
                        <tr>
                          <td colSpan={2}>… {preview.count - preview.head.length - preview.tail.length} more …</td>
                        </tr>
                      )}
                      {preview.tail.map((r) => (
                        <tr key={r.qr}>
                          <td>{r.unique}</td>
                          <td><code>{r.qr}</code></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            <div className={styles.inline}>
              <button type="submit" className={preview ? styles.secondary : styles.primary} disabled={busy || !quantity}>
                {preview ? 'Preview again' : busy ? 'Checking…' : 'Preview'}
              </button>
              {preview && preview.count > 0 && (
                <button type="button" className={styles.primary} disabled={busy} onClick={make}>
                  {busy ? 'Making…' : `Make ${preview.count} ${preview.kind === 'bin' ? 'bin' : 'item'} tags`}
                </button>
              )}
            </div>
          </form>

          {error && <p className={styles.error}>{error}</p>}
          {!data && !error && <p className={styles.lead}>Loading…</p>}
          {data && (
            <div className={styles.tableCard}>
              {data.batches.length === 0 ? (
                <div className={styles.empty}>No tag stock made yet.</div>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Made</th>
                      <th>Type</th>
                      <th>Tags</th>
                      <th>Count</th>
                      <th>Claimed</th>
                      <th>Unused</th>
                      <th>Claimed by</th>
                      <th>Note</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.batches.map((b) => (
                      <tr key={b.batch_id}>
                        <td title={b.created_by || undefined}>{dateText(b.created_at)}</td>
                        <td>{b.kind === 'bin' ? 'Bin' : 'Item'}</td>
                        <td>
                          {b.first}–{b.last}
                        </td>
                        <td>{b.count}</td>
                        <td>{b.claimed}</td>
                        <td>{b.unused}</td>
                        <td>{b.claimed_by.map((c) => `${c.name} (${c.count})`).join(', ') || '—'}</td>
                        <td>{b.note || '—'}</td>
                        <td>
                          <button type="button" className={styles.secondary} onClick={() => download(b.batch_id)}>
                            Download sheet
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </PageBody>
    </>
  )
}
